import { Subtitles } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

const pillars = [
  {
    title: "Farm",
    subtitle:"Agriculture Exchange Network",
    to: "/farm",
    text:
      "A living marketplace where farmers, households, and local businesses exchange value with clarity. Not a noisy bazaar, but a trusted field of real supply and real demand.",
  },
  {
    title: "FarmPulse",
    subtitle:"Agriculture Intelligence Engine",
    to: "/farmpulse",
    text:
      "Market intelligence built for reality. Signals, trends, and context that help farmers decide before risk becomes loss.",
  },
  {
    title: "FarmMark",
    subtitle:"Impact Verification Layer",
    to: "/farmmark",
    text:
      "Impact as evidence, not slogans. Every action is tracked into measurable environmental outcomes, resource efficiency, and community resilience.",
  },
  {
    title: "Orrith Core",
    to: "/orrith-landing",
    text:
      "The intelligence foundation of Orrith. Combining environmental data, agricultural signals, and machine intelligence to understand and strengthen planetary food systems."
  }
];

function NarrativeParagraph({ children }) {
  return <p className="text-[1.03rem] leading-8 text-slate-700 dark:text-slate-200">{children}</p>;
}

export default function OurPlatform() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      <section className="relative overflow-hidden border-b border-gray-200 dark:border-neutral-800 bg-gradient-to-r from-[#14532d] via-[#166534] to-[#16a34a]">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" aria-hidden="true">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="platform-header-grid" width="32" height="32" patternUnits="userSpaceOnUse">
                <path d="M 32 0 L 0 0 0 32" fill="none" stroke="white" strokeWidth="0.6" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#platform-header-grid)" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-16 lg:py-20">
          <div className="max-w-4xl mx-auto text-center">
              <h1
                className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white"
              >
                Architecting the Cycle of Certainty
              </h1>

              <p className="mt-5 max-w-3xl mx-auto text-lg leading-8 text-emerald-50/95">
                Orrith is an agriculture intelligence infrastructure engineered to coordinate food systems, strengthen economic trust, and protect planetary continuity. Beyond the application layer, we are building a data-driven operating layer that connects production,
                markets, logistics, and environmental intelligence into one adaptive network.
              </p>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-12">
        <section className="rounded-2xl border border-slate-200/80 dark:border-neutral-800 bg-white/85 dark:bg-neutral-900 backdrop-blur-sm p-6 sm:p-10 shadow-[0_10px_40px_rgba(2,6,23,0.08)] dark:shadow-[0_12px_45px_rgba(0,0,0,0.35)]">
          <div className="space-y-6">
            <NarrativeParagraph>
              We began with a painful observation that repeats itself in silence across cities and villages: food is
              lost not because the earth failed, but because coordination failed. A farmer wakes before sunrise,
              invests labor, seed, transport, and hope, then watches produce lose value by the hour because the
              right buyer never meets the right harvest at the right moment. At the same time, households,
              schools, clinics, and shelters navigate scarcity, while edible food is discarded nearby. This is not
              only an efficiency failure. It is a moral failure of systems design.
            </NarrativeParagraph>

            <NarrativeParagraph>
              Orrith exists to end that fragmentation. We did not set out to build another marketplace page,
              another fragmented digital tool disconnected from lived reality. We set out to build continuity,
              where every transaction can evolve into impact, every data point can become foresight, and every
              community member can participate with dignity. We call this the Cycle of Certainty: a practical,
              connected path from production to exchange, from exchange to nourishment, and from nourishment to
              measurable societal and environmental progress.
            </NarrativeParagraph>

            <NarrativeParagraph>
              In this platform, the farmer is not reduced to a profile card. The donor is not reduced to a
              one-time gesture. The buyer is not reduced to a checkout event. Each participant is part of a living
              network with memory, context, and consequence. We designed Orrith so that trust is not a slogan;
              it is an operational property. Listings are contextual. Conversations are traceable. Claims are
              accountable. Policies are visible. Signals are transparent. When communities can see clearly, they
              can decide wisely.
            </NarrativeParagraph>

            <NarrativeParagraph>
              Orrith is a living ecosystem designed to bridge the gap between human survival and planetary health through
              a Cycle of Certainty. We aimed to build far more than a digital tool; it is a restorative infrastructure where
              every surplus becomes a resource and every data point becomes a guarantee for the future. Orrith creates
              a trusted agricultural exchange network where produces, buyers, logistics providers, and communities coordinate
              supply and demand with greater visibility. FarmPulse transforms agricultural data into actionable intelligence,
              helping farmers and stakeholders anticipate risks, understand market movement, and make better decisions.
              By weaving together agriculture, data, and nature, Orrith ensures that the cycles of the Earth soil health, water conservation,
              and food production are no longer left to chance, but are secured in a continuous loop of growth and environmental continuity.
            </NarrativeParagraph>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2">
          {pillars.map((pillar) => (
            <Link
              key={pillar.title}
              to={pillar.to}
              className="group rounded-2xl border border-slate-200 dark:border-neutral-800 bg-white/90 dark:bg-neutral-900 p-5 hover:border-emerald-500/60 dark:hover:border-emerald-400/60 hover:shadow-lg transition no-underline"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="font-semibold text-base text-emerald-700 dark:text-green-300">{pillar.title}</span>
                <span className="text-xs font-semibold text-emerald-700 dark:text-green-300">Open</span>
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">{pillar.text}</p>
            </Link>
          ))}
        </section>

        <section className="rounded-2xl border border-emerald-200/80 dark:border-emerald-900/60 bg-gradient-to-br from-emerald-50 via-white to-cyan-50 dark:from-emerald-950/30 dark:via-neutral-900 dark:to-neutral-900 p-6 sm:p-10">
          <h2
            className="text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-100"
          >
            Why This Model Matters
          </h2>

          <div className="mt-5 space-y-6">
            <NarrativeParagraph>
              Traditional digital platforms often isolate functions: one app for trading, another for relief, another
               for analytics, another for reporting. Fragmentation introduces delay, and delay in food systems costs
               people money, nutrition, and resilience. Orrith is intentionally integrated. The same ecosystem that
               helps move produce through Farm and track environmental outcomes through FarmMark can inform FarmMark impact records. The same market behavior that affects today's listing can improve
               tomorrow's planting strategy through FarmPulse. This is not convenience layering. It is systems
               coherence.
            </NarrativeParagraph>

            <NarrativeParagraph>
              We also reject the false tradeoff between compassion and rigor. A successful trade is an act of care, but it
               still requires governance: offer integrity, moderation controls, transparent status flow, and clear
               eligibility context. A marketplace is an engine of income, but it still requires human safeguards:
              structured conversations, offer records, and community trust standards. An impact score is meaningful
              only when its underlying events are verifiable. For us, humanity and discipline are not opposites.
              They are the pair that makes a platform worthy of public trust.
            </NarrativeParagraph>

            <NarrativeParagraph>
              Environmental accountability is equally central. Food waste is climate waste. Every avoided spoilage
              event protects embedded resources: water, labor, transport, soil nutrients, and energy. Every
              successful redistribution lowers pressure on emergency systems and reduces unnecessary production
              shocks. By connecting food movement with outcome tracking, Orrith turns scattered local effort into a
              cumulative climate strategy. Not abstract ambition, but daily measurable change.
            </NarrativeParagraph>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 dark:border-neutral-800 bg-white/90 dark:bg-neutral-900 p-6 sm:p-10">
          <h2
            className="text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-100"
          >
            The Commitment Behind The Platform
          </h2>

          <div className="mt-5 space-y-6">
            <NarrativeParagraph>
              Our commitment is simple to say and difficult to execute: make the right action easier than the wrong
              action. It should be easier to list responsibly than to speculate blindly. Easier to route surplus than
              to waste it. Easier to verify impact than to claim it without proof. Easier to ask for help than to
              disappear into silence. Every feature, policy page, and workflow in Orrith exists to serve that
              commitment.
            </NarrativeParagraph>

            <NarrativeParagraph>
              We design for people who carry real constraints: uncertain weather, unstable pricing, transport
              bottlenecks, limited capital, and unpredictable demand. That is why we value clarity over complexity,
              flow over friction, and resilience over theatrics. We choose language that respects people. We choose
              interfaces that reduce confusion. We choose governance that makes participation safer, not heavier.
              In short: we build for lived reality, not demo perfection.
            </NarrativeParagraph>

            <NarrativeParagraph>
              The future we are building is not a distant promise. It is already visible in every successful offer,
              every producer who finds a market in time, every household that receives produce before urgency
              becomes crisis, and every community leader who can finally see impact as a living ledger. Orrith
              is the shared operating layer for that future.
            </NarrativeParagraph>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            <Link to="/farm" className="rounded-xl border border-slate-300 dark:border-slate-600 px-4 py-3 text-sm font-semibold text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200 no-underline transition">
              Enter Farm
            </Link>
            <Link to="/north-star" className="rounded-xl border border-slate-300 dark:border-slate-600 px-4 py-3 text-sm font-semibold text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200 no-underline transition">
              Visit NorthStar
            </Link>
          </div>
        </section>

        <section className="pb-2" />
      </main>
    </div>
  );
}
